/**
 * Parameters for fetching game statistics.
 * Requires either a game ID or slug to identify which game to retrieve statistics for.
 */
interface FetchParams {
  /** Numeric game ID to fetch statistics for */
  id?: number;
  /** String identifier for the game in format 'provider/game-name' */
  slug?: string;
}

/**
 * Game Statistics Composable
 *
 * This composable provides reactive state and methods to fetch and manage statistical data
 * for casino games. It enables components to access betting insights and other game statistics
 * retrieved from the API.
 *
 * The composable maintains loading states, error handling, and data caching for each operation.
 *
 * @see {@link https://cactuscorp.atlassian.net/wiki/x/AwD2Eg | Complete Documentation and Use}.
 *
 * @returns {Object} Collection of reactive state and methods
 * @property {Ref<boolean>} `isLoadingStatistics` - Reactive ref tracking the state of statistics data fetching.
 * @property {Ref<string|null>} `statisticsError` - Error message for failures in the statistics API request.
 * @property {Function} `fetchGameStatisticsById` - Method to load statistics using a game's numeric ID.
 * @property {Function} `fetchGameStatisticsBySlug` - Method to load statistics using a game's string identifier.
 *
 * @example
 * // In a Vue component
 * const { fetchGameStatisticsBySlug, isLoadingStatistics } = useGamesStatistics();
 *
 * // Fetch statistics for a specific game
 * onMounted(async () => {
 *   await fetchGameStatisticsBySlug('provider/game-name');
 * });
 */
export function useGamesStatistics() {
  const { ApiService } = useApiService();

  const isLoadingStatistics = ref(false);
  const statisticsError = ref<string | null>(null);

  /**
   * Core method to fetch game statistics based on provided identifiers
   *
   * This internal method handles the actual API call to retrieve game statistics.
   * It constructs the appropriate query parameters and manages loading states and error handling.
   *
   * @param params - Object containing game ID or slug (or both)
   * @returns Promise resolving to the fetched game statistics data
   *
   * @example
   * // Fetch statistics using a game ID
   * fetchGameStatistics({ id: 123 });
   *
   * // Fetch statistics using a game slug
   * fetchGameStatistics({ slug: 'provider/game-name' });
   */
  const fetchGameStatistics = async (params: FetchParams) => {
    isLoadingStatistics.value = true;
    statisticsError.value = null;

    const queryParams = new URLSearchParams();

    if (params.id !== undefined) queryParams.append('id', params.id.toString());
    if (params.slug !== undefined) queryParams.append('slug', params.slug);

    try {
      const response = await ApiService.get<GameStatisticsByPeriod>(
        `/bff/games/statistics?${queryParams.toString()}`,
      );

      return response.data;
    } catch (err: any) {
      statisticsError.value = err.message || 'Error fetching game statistics';
      throw err;
    } finally {
      isLoadingStatistics.value = false;
    }
  };

  /**
   * Convenience method to fetch game statistics using only a game ID
   *
   * This is a wrapper around fetchGameStatistics that simplifies the API
   * for cases where only the numeric ID is available.
   *
   * @param gameId - Numeric ID of the game to fetch statistics for
   * @returns Promise resolving to the fetched game statistics
   *
   * @example
   * const { fetchGameStatisticsById } = useGamesStatistics();
   * const gameStats = await fetchGameStatisticsById(123);
   */
  const fetchGameStatisticsById = (gameId: number) => {
    return fetchGameStatistics({ id: gameId });
  };

  /**
   * Convenience method to fetch game statistics using only a game slug
   *
   * This is a wrapper around fetchGameStatistics that simplifies the API
   * for cases where only the string identifier is available.
   *
   * @param gameSlug - String identifier in format 'provider/game-name'
   * @returns Promise resolving to the fetched game statistics
   *
   * @example
   * const { fetchGameStatisticsBySlug } = useGamesStatistics();
   * const gameStats = await fetchGameStatisticsBySlug('pragmatic/sweet-bonanza');
   */
  const fetchGameStatisticsBySlug = (gameSlug: string) => {
    return fetchGameStatistics({ slug: gameSlug });
  };

  return {
    isLoadingStatistics,
    statisticsError,
    fetchGameStatisticsById,
    fetchGameStatisticsBySlug,
  };
}
